<template>
  <div class="app-container pt-4">
    <!-- Left Sidebar -->
    <div class="sidebar">
      <div class="model-selection">
        <h4>Select LLM Model</h4>
        <select v-model="selectedModel">
          <option value="langGpt4all">langGpt4all</option>
          <option value="openAi">openAi</option>
        </select>
      </div>
    </div>

    <!-- Right Content Area -->
    <div class="content">
      <div class="chat-container">
        <div class="chat-messages" ref="chatMessagesRef">
          <div
            v-for="(message, index) in messages"
            :key="index"
            :class="{
              'message-user': message.role === 'user',
              'message-assistant': message.role === 'assistant'
            }"
          >
            <div class="message-content">
              <strong v-if="message.role === 'user'">User:</strong>
              <strong v-else>PROne LLM:</strong>
              <p>{{ message.content }}</p>
            </div>
          </div>
        </div>
        <div class="chat-input">
          <input
            type="text"
            placeholder="대화를 입력해주세요."
            v-model="userInput"
            @keyup.enter="sendMessage"
            :disabled="isLoading"
          />
          <button @click="sendMessage" :disabled="isLoading">Send</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';

export default defineComponent({
  name: 'TwoFrameChatView',

  props: {
    selectedJobs: {
      type: Array,
      default: () => [],
    },
    sessionId: {
      type: Number,
      default: null,
    },
  },

  setup(props) {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    // Refs
    const userInput = ref('');
    const selectedModel = ref('openAi');
    const chatMessagesRef = ref(null);
    const messages = computed(() => store.state.chat.messages);
    const isLoading = computed(() => store.state.chat.isLoading);
    const error = computed(() => store.state.chat.error);
    const sessionId = computed(() => store.state.chat.sessionId);

    // Methods
    const scrollToBottom = () => {
      if (chatMessagesRef.value) {
        chatMessagesRef.value.scrollTop = chatMessagesRef.value.scrollHeight;
      }
    };

    const initializeChat = async () => {
      try {
        await store.dispatch('chat/initializeChat', {
          selectedJobs: props.selectedJobs,
          sessionId: props.sessionId,
        });
      } catch (err) {
        console.error('Failed to initialize chat:', err);
      }
    };

    const sendMessage = async () => {
      if (userInput.value.trim() === '') return;

      try {
        await store.dispatch('chat/sendMessage', userInput.value);
        userInput.value = '';
        scrollToBottom();
      } catch (err) {
        console.error('Failed to send message:', err);
      }
    };

    // Watchers
    watch(messages, () => {
      scrollToBottom();
    });

    // Lifecycle hooks
    onMounted(() => {
      initializeChat();
    });

    return {
      userInput,
      selectedModel,
      messages,
      isLoading,
      error,
      sendMessage,
      chatMessagesRef,
    };
  },
});
</script>

<style scoped>
.app-container {
  display: flex;
  min-height: calc(100vh - 120px);
  font-family: Arial, sans-serif;
}

.sidebar {
  min-width: 250px;
  width: fit-content;
  max-width: 300px;
  background-color: #f0f0f0;
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 20px;
}

.model-selection {
  margin-bottom: 20px;
}

.model-selection select {
  width: 100%;
  padding: 5px;
  font-size: 1em;
}

.content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  background-color: #faf0e6;
  padding: 20px;
}

.chat-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex-grow: 1;
  background-color: #ffffff;
  border: 1px solid #cccccc;
  border-radius: 5px;
  margin-bottom: 20px;
  padding: 20px;
  overflow-y: auto;
  min-height: 200px;
}

.chat-input {
  display: flex;
}

.chat-input input {
  flex-grow: 1;
  padding: 10px;
  border: 1px solid #cccccc;
  border-radius: 5px 0 0 5px;
}

.chat-input button {
  padding: 10px 20px;
  background-color: #007bff;
  color: #ffffff;
  border: none;
  border-radius: 0 5px 5px 0;
  cursor: pointer;
}

.chat-input button:disabled,
.chat-input input:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.message-user {
  text-align: right;
}

.message-assistant {
  text-align: left;
}

.message-content {
  display: inline-block;
  max-width: 70%;
  word-wrap: break-word;
  padding: 10px;
  border-radius: 10px;
  margin-bottom: 10px;
}

.message-user .message-content {
  background-color: #dcf8c6;
}

.message-assistant .message-content {
  background-color: #ececec;
}

.project-list label {
  display: block;
  margin-bottom: 10px;
  font-size: 0.9em;
}
</style>